/**
 * Who to show as the actor on a timeline entry.
 *
 * The backend's `principal` abstraction IS the privacy boundary. Automated and
 * platform actors run under internal identities — a platform administrator's
 * name, a service account — and surfacing those to a mine operator leaks staff
 * identity into a customer-facing screen. Only real operator/supervisor
 * principals get their provided label; everything else collapses to a safe
 * generic one.
 *
 * Never reconstruct an identity from ids or other fields to work around this.
 */
import i18n from "@/shared/lib/i18n/index.ts";
import type { AlertActivityPrincipal } from "@/alerts/types/alerts-workspace";

export interface ActorDisplay {
  label: string;
  /** True when this is a named human, so the UI can style it differently. */
  isPerson: boolean;
}

/** Principal kinds whose provided label is safe to show verbatim. */
const NAMED_KINDS = new Set(["operator", "supervisor", "user"]);

/** Principal kind → the generic label's key. The label itself is translated. */
const GENERIC_LABEL_KEYS = {
  automated: "activity.actor.automated",
  automation: "activity.actor.automated",
  system: "activity.actor.system",
  platform: "activity.actor.system",
  service: "activity.actor.system",
} as const;

const unknownActor = (): ActorDisplay => ({
  label: i18n.t("activity.actor.unknown", { ns: "alerts" }),
  isPerson: false,
});

export const describeActor = (principal: AlertActivityPrincipal | null | undefined): ActorDisplay => {
  // Older activity rows predate `principal`. Unknown is an honest answer;
  // failing to render the timeline over it is not.
  if (!principal) return unknownActor();

  const kind = principal.kind?.toLowerCase() ?? "";
  if (NAMED_KINDS.has(kind) && principal.label?.trim()) {
    return { label: principal.label.trim(), isPerson: true };
  }
  const genericKey = GENERIC_LABEL_KEYS[kind as keyof typeof GENERIC_LABEL_KEYS];
  if (genericKey) return { label: i18n.t(genericKey, { ns: "alerts" }), isPerson: false };

  // An unrecognised kind is treated as internal, not as a person: defaulting to
  // "show the label" would leak exactly what this function exists to protect.
  return unknownActor();
};

/**
 * Operator-readable wording for an activity kind. Plain operational language —
 * never internal vocabulary like "projection updated" or "state reconciled".
 */
export const describeActivityKind = (kind: string): string => {
  switch (kind) {
    case "confirm_signal":
      return i18n.t("activity.kind.confirmSignal", { ns: "alerts" });
    case "close_alert":
      return i18n.t("activity.kind.closeAlert", { ns: "alerts" });
    case "correct_result":
      return i18n.t("activity.kind.correctResult", { ns: "alerts" });
    case "add_note":
      return i18n.t("activity.kind.addNote", { ns: "alerts" });
    case "create":
      return i18n.t("activity.kind.create", { ns: "alerts" });
    case "reopen":
      return i18n.t("activity.kind.reopen", { ns: "alerts" });
    default:
      // Turn an unknown snake_case kind into something readable rather than
      // showing the raw token.
      return kind.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  }
};
